import type { ObjectConfig } from "@homebound/form-state";
import { observableRef } from "mobx";
import type { DisplayNamedFragment, NamedFragment } from "src/fragments/commonFragments.generated";
import {
  ChangeEventLineItemType,
  ChangeEventStatus,
  ChangeEventType,
  type CostConfidence,
  type CostType,
  type Maybe,
  type SaveChangeEventLineItemInput,
} from "src/generated/graphqlTypes";
import { Selectable } from "src/models/Selectable";
import type { ChangeEventLineItemsTabChangeEventLineItemFragment } from "src/routes/projects/change-events/ChangeEventLineItemsTab.generated";
import type { ChangeEventLineItemTradePartnerSelect_BidItemBidContractLineItemsFragment } from "src/routes/projects/change-events/components/ChangeEventLineItemTradePartnerSelect.generated";
import { calculateMarkup } from "src/utils";
import { makeSimpleAutoObservable } from "src/utils/makeSimpleAutoObservable";
import { CostSourceType } from "../ChangeEventLineItemsTable";

/**
 * Holds a change event line item's editable values and calculates its quantity, cost, and price changes.
 *
 * Autosave TL;DR:
 * - UI edits update this observable row; numeric/text inputs buffer typing until blur.
 * - useFormStates caches observable rows and owns their reconciliation and autosave.
 * - Server fragments go through form-state reconciliation, which updates this row and its baseline while
 *   preserving pending user edits. Accepted values and their baseline change together, so clean server
 *   updates do not echo-save.
 * - Read-only context also refreshes through the form but is excluded from the mutation input.
 */
export class ObservableChangeEventLineItem extends Selectable {
  totalPriceInCents!: number;
  totalCostInCents!: number;
  quantity: Maybe<number>;
  unitOfMeasureId?: string;
  costConfidence!: CostConfidence;
  tradePartnerId?: string | null;
  proposedBidItemId?: string | null;
  proposedTaskId?: string | null;
  proposedBidContractLineItemId?: string | null;
  proposedPackageDesignNotes?: string;
  fragment: ChangeEventLineItemsTabChangeEventLineItemFragment;

  // Selection objects supply labels while the server still has the previously selected ID.
  selectedTradePartner?: NamedFragment;
  selectedBidItem?: DisplayNamedFragment;
  selectedTask?: NamedFragment;
  selectedBidContractLineItem?: ChangeEventLineItemTradePartnerSelect_BidItemBidContractLineItemsFragment;
  costSourceOverride?: { fragment: ChangeEventLineItemsTabChangeEventLineItemFragment; value: CostSourceType };

  constructor(celi: ChangeEventLineItemsTabChangeEventLineItemFragment) {
    super(celi.id, false);
    this.fragment = celi;
    this.totalPriceInCents = celi.totalPriceInCents;
    this.totalCostInCents = celi.totalCostInCents;
    this.quantity = celi.quantity;
    this.costConfidence = celi.costConfidenceDetail.code;
    this.tradePartnerId = celi.tradePartner?.id;
    this.proposedBidItemId = celi.proposedBidItem?.id;
    this.proposedBidContractLineItemId = celi.proposedBidContractLineItem?.id;
    this.unitOfMeasureId = celi.unitOfMeasure?.id;
    this.proposedTaskId = celi.proposedTask?.id;
    this.proposedPackageDesignNotes = celi.proposedPackageDesignNotes ?? undefined;

    makeSimpleAutoObservable(this, {
      selectedBidContractLineItem: observableRef,
      costSourceOverride: observableRef,
      fragment: observableRef,
    });
  }

  // Read-only context follows the form's latest fragment without another server-to-model update path.
  get isInternal() {
    return this.fragment.changeEvent.type.code === ChangeEventType.Internal;
  }

  get name() {
    return this.fragment.name;
  }
  get costType(): CostType {
    return this.fragment.costType;
  }
  get type() {
    return this.fragment.type;
  }
  get unitOfMeasure() {
    return this.fragment.unitOfMeasure;
  }
  get item() {
    return this.fragment.item;
  }
  get projectItemId() {
    return this.fragment.projectItem.id;
  }
  get bidItemTemplateItemId() {
    return (
      this.fragment.itemTemplateItem?.bidItemTemplateItem?.id ||
      this.fragment.projectItem.itemTemplateItem?.bidItemTemplateItem?.id
    );
  }
  get developmentId() {
    return this.fragment.projectItem.project.cohort?.development?.id;
  }
  get locationName() {
    return this.fragment.location?.displayLocationPath;
  }
  get locationId() {
    return this.fragment.location?.id;
  }
  get originalTotalCostInCents() {
    return this.fragment.originalTotalCostInCents;
  }
  get originalTotalPriceInCents() {
    return this.fragment.originalTotalPriceInCents;
  }
  get originalQuantity() {
    return this.fragment.originalQuantity;
  }
  get originalTradePartner() {
    return this.fragment.originalTradePartner;
  }
  get allCommittedCostInCents() {
    return this.fragment.projectItem.budgetFinancials.committed;
  }
  get originalBidItem() {
    return this.fragment.originalBidItem;
  }
  get originalTask() {
    return this.fragment.originalTask ?? undefined;
  }
  get originalBidContractLineItem() {
    return this.fragment.originalBidContractLineItem ?? undefined;
  }
  get originalPackageDesignNotes() {
    return this.fragment.originalPackageDesignNotes ?? undefined;
  }
  get isMetadataChangeOnly() {
    return this.fragment.isMetadataChangeOnly;
  }

  /** Prefer acknowledged display objects, retaining the user's choice while its ID is still pending. */
  get tradePartner() {
    return selectedRelation(this.tradePartnerId, this.fragment.tradePartner, this.selectedTradePartner);
  }
  set tradePartner(value: NamedFragment | undefined) {
    this.selectedTradePartner = value;
  }
  get proposedBidItem() {
    return selectedRelation(this.proposedBidItemId, this.fragment.proposedBidItem, this.selectedBidItem);
  }
  set proposedBidItem(value: DisplayNamedFragment | undefined) {
    this.selectedBidItem = value;
  }
  get proposedTask() {
    return selectedRelation(this.proposedTaskId, this.fragment.proposedTask, this.selectedTask);
  }
  set proposedTask(value: NamedFragment | undefined) {
    this.selectedTask = value;
  }
  get proposedBidContractLineItem() {
    return selectedRelation(
      this.proposedBidContractLineItemId,
      this.fragment.proposedBidContractLineItem,
      this.selectedBidContractLineItem,
    );
  }
  set proposedBidContractLineItem(
    value: ChangeEventLineItemTradePartnerSelect_BidItemBidContractLineItemsFragment | undefined,
  ) {
    this.selectedBidContractLineItem = value;
  }

  /** Keep a user-chosen cost-source mode until the next server snapshot, as the old refresh did. */
  get costSource() {
    return this.costSourceOverride?.fragment === this.fragment
      ? this.costSourceOverride.value
      : this.proposedBidContractLineItemId
        ? CostSourceType.BID_CONTRACT
        : CostSourceType.MANUAL;
  }
  set costSource(value: CostSourceType) {
    this.costSourceOverride = { fragment: this.fragment, value };
  }

  /**
   * Stitch together the PI's current plan info w/the CELI's plan info, keying
   * by the identityId so that "old PI add <-> new PI add" line up,
   * "old PI modify for option A <-> new PI modify for option A" line up, with
   * the caveat that both sides are optional, but at least one side must be present.
   */
  get itiRows() {
    const celiItisByIdentityId = [this.fragment.itemTemplateItem, ...this.fragment.modifyingTemplateItems]
      .compact()
      .keyBy((iti) => iti.identityId);
    const piItisByOriginalId = [
      this.fragment.projectItem.itemTemplateItem,
      ...this.fragment.projectItem.modifyingTemplateItems,
    ]
      .compact()
      .keyBy((iti) => iti.identityId);

    return [...Object.keys(celiItisByIdentityId), ...Object.keys(piItisByOriginalId)]
      .unique()
      .map((id) => ({ originalItiId: id, piIti: piItisByOriginalId[id], celiIti: celiItisByIdentityId[id] }));
  }

  get disableMarkup(): boolean {
    return this.fragment.projectItem.project.canEditPrice.allowed === false;
  }

  get projectMarkupBasisPoints(): number {
    return this.fragment.projectItem.projectMarkupBasisPoints;
  }

  get originalUnitCostInCents() {
    return this.originalQuantity ? Math.floor(this.originalTotalCostInCents / this.originalQuantity) : 0;
  }

  get originalMarkupAmount() {
    return calculateMarkup(this.originalTotalCostInCents, this.originalTotalPriceInCents).markupAmount;
  }

  get originalMarkupPercent() {
    return calculateMarkup(this.originalTotalCostInCents, this.originalTotalPriceInCents).markupPercentage;
  }

  /**
   * A `Remove` CELI on a still-pending change event.
   *
   * While pending, a Remove's stored deltas aren't meaningful — the backend recomputes them from
   * the live project item at acceptance. So we preview "remove the PI's current value": the change
   * shows the current value inverted and the proposed values land at zero, all read-only.
   * Once accepted, the stored deltas are the real values and we display them as-is.
   */
  get isPendingRemove() {
    return (
      this.type === ChangeEventLineItemType.Remove && this.fragment.changeEvent.status !== ChangeEventStatus.Accepted
    );
  }

  /** The quantity change to display; pending removes preview removing the PI's full current quantity. */
  get quantityChange(): Maybe<number> {
    return this.isPendingRemove ? -(this.originalQuantity ?? 0) : this.quantity;
  }

  /** The total cost change to display; pending removes preview zeroing out the PI's current cost. */
  get totalCostChangeInCents() {
    return this.isPendingRemove ? -this.originalTotalCostInCents : this.totalCostInCents;
  }

  /** The total price change to display; pending removes preview zeroing out the PI's current price. */
  get totalPriceChangeInCents() {
    return this.isPendingRemove ? -this.originalTotalPriceInCents : this.totalPriceInCents;
  }

  get proposedQuantity() {
    return (this.originalQuantity || 0) + (this.quantityChange || 0);
  }

  get proposedUnitCostInCents() {
    return this.proposedQuantity ? Math.round(this.proposedTotalCostInCents / this.proposedQuantity) : 0;
  }

  get proposedMarkupAmount() {
    return calculateMarkup(this.proposedTotalCostInCents, this.proposedTotalPriceInCents).markupAmount;
  }

  get proposedMarkupPercent() {
    // Show contract markup when auto-calculated price as it can might have some markup difference
    // E.g. some rounded number will make the markup go up/down a few basis points (e.g. 10.00% vs 10.01)
    if (this.disableMarkup && this.totalPriceInCents !== 0) return this.projectMarkupBasisPoints / 100;
    return calculateMarkup(this.proposedTotalCostInCents, this.proposedTotalPriceInCents).markupPercentage;
  }

  get proposedTotalCostInCents() {
    return this.originalTotalCostInCents + this.totalCostChangeInCents;
  }

  get proposedTotalPriceInCents() {
    return this.originalTotalPriceInCents + this.totalPriceChangeInCents;
  }

  get unitCostChangeInCents() {
    return this.proposedUnitCostInCents - this.originalUnitCostInCents;
  }

  updateProposedTradePartner(tradePartner?: NamedFragment) {
    this.tradePartner = tradePartner;
    this.tradePartnerId = tradePartner?.id;
  }

  updateChangeInQuantity(diff: number) {
    const proposedQuantity = (this.originalQuantity || 0) + (diff || 0);
    this.updateProposedQuantity(proposedQuantity);
  }

  updateProposedQuantity(proposedQty: number) {
    // impacts PQ which impacts PUC??, PTC, PTP
    const currentProposedQty = (this.originalQuantity || 0) + (this.quantity || 0);

    // proposedTotalCostInCents / Proposed QTY = proposedUnitCostInCents
    // proposedUnitCostInCents * new value = proposedTotalCostInCents
    // proposedTotalCostInCents - originalTotalCostInCents = totalCostInCents
    const cost = currentProposedQty
      ? Math.round(proposedQty * ((this.originalTotalCostInCents + this.totalCostInCents) / currentProposedQty))
      : Math.round(proposedQty);
    const totalCost = Math.round(cost - this.originalTotalCostInCents);

    // proposedTotalCostInCents * (current proposedMarkupPecent / 100) + proposedTotalCostInCents = proposedTotalPriceInCents
    // proposedTotalPriceInCents - originalTotalPriceInCents = totalPriceInCents
    const price = Math.round(cost * (this.proposedMarkupPercent / 100) + cost);
    const totalPrice = Math.round(price - this.originalTotalPriceInCents);

    if (!this.isInternal) {
      this.totalPriceInCents = totalPrice;
    }
    this.totalCostInCents = totalCost;
    this.quantity = proposedQty - (this.originalQuantity || 0);

    // Currently when we update the quantity to 0, we lose the unit cost. Therefore, we no longer have the proposed BCLI costs associated with the CELI.
    // TODO/Follow-up: make the unit cost persist when the quantity is changed to 0.
    if (proposedQty === 0) {
      this.updateCostSource(CostSourceType.MANUAL);
    }
  }

  updateProposedUnitCostInCents(proposedUnitCost: number, bcliId?: string) {
    if (bcliId) {
      this.proposedBidContractLineItemId = bcliId;
      if (this.quantity === 0) {
        this.quantity = 1;
      }
    }

    // impacts PUC which impacts PTC, PTP
    const newProposedTotalCost = this.proposedQuantity
      ? Math.round(proposedUnitCost * this.proposedQuantity)
      : Math.round(proposedUnitCost);
    const totalCost = Math.round(newProposedTotalCost - this.originalTotalCostInCents);

    const newProposedTotalPrice = Math.round(
      newProposedTotalCost * (this.proposedMarkupPercent / 100) + newProposedTotalCost,
    );
    const totalPrice = Math.round(newProposedTotalPrice - this.originalTotalPriceInCents);

    if (!this.isInternal) {
      this.totalPriceInCents = totalPrice;
    }
    this.totalCostInCents = totalCost;
  }

  updateProposedMarkupPercent(proposedMarkupPercent: number) {
    const newProposedTotalPrice = Math.round(
      this.proposedTotalCostInCents * (proposedMarkupPercent / 100) + this.proposedTotalCostInCents,
    );
    this.totalPriceInCents = newProposedTotalPrice - this.originalTotalPriceInCents;
  }

  updateChangeInTotalCostInCents(diff: number | undefined) {
    const proposed = this.originalTotalCostInCents + (diff ?? 0);
    this.updateProposedTotalCostInCents(proposed);
  }

  updateProposedTotalCostInCents(proposedTotalCost: number) {
    // impacts PTC which impacts PUC, PTP
    if (!this.isInternal) {
      // Calculate the proposed total price based on the proposed total cost and the proposed markup percent when the markup is not disabled meaning the project can edit price
      if (!this.disableMarkup) {
        this.totalPriceInCents =
          Math.round(proposedTotalCost * (this.proposedMarkupPercent / 100) + proposedTotalCost) -
          this.originalTotalPriceInCents;
      }
    }
    this.totalCostInCents = proposedTotalCost - this.originalTotalCostInCents;
  }

  updateChangeInTotalPriceInCents(diff: number | undefined) {
    if (diff) {
      this.totalPriceInCents = diff;
    }
  }

  updateProposedTotalPriceInCents(proposedTotalPrice: number | undefined) {
    if (proposedTotalPrice) {
      // impacts PTP
      this.totalPriceInCents = proposedTotalPrice - this.originalTotalPriceInCents;
    }
  }

  updateUnitOfMeasureId(unitOfMeasureId?: string) {
    this.unitOfMeasureId = unitOfMeasureId;
  }

  updateProposedBidItem(bidItem: DisplayNamedFragment | undefined) {
    this.proposedBidItem = bidItem;
    this.proposedBidItemId = bidItem?.id ?? null;
    // Assume the BCLI needs to be reassigned
    this.updateProposedBidContractLineItem(undefined);
    // Only do this here, b/c if we call it in `updateProposedBidContractLineItem`, then cost source
    // can never change/drift from the derived value
    this.deriveCostSource();
  }

  updateProposedTask(task: NamedFragment | undefined) {
    this.proposedTask = task;
    this.proposedTaskId = task?.id ?? null;
  }

  updateCostSource(costSource: CostSourceType) {
    this.costSource = costSource;
    // If the cost source is changed to manual, remove the proposed BCLI
    if (costSource === CostSourceType.MANUAL) {
      this.updateProposedBidContractLineItem(undefined);
    }
  }

  updateProposedBidContractLineItem(
    bcli: ChangeEventLineItemTradePartnerSelect_BidItemBidContractLineItemsFragment | undefined,
  ) {
    this.tradePartnerId = bcli?.revision.bidContract.tradePartner?.id ?? null;
    this.proposedBidContractLineItemId = bcli?.id ?? null;
    this.proposedBidContractLineItem = bcli;
    if (bcli) {
      // Bid Item BCLIs `totalCostInCents` is an itemized cost, i.e. total cost === unit cost
      this.updateProposedUnitCostInCents(bcli.totalCostInCents, bcli.id);
    }
  }

  updateProposedPackageDesignNotes(packageDesignNotes: string | undefined) {
    this.proposedPackageDesignNotes = packageDesignNotes;
  }

  private deriveCostSource() {
    // Unlike other "current values", where we do `original BI || proposed BI`, we always look at
    // proposed BCLI for the cost source, b/c we want to allow the user to turn it on/off like a toggle
    this.costSource = this.proposedBidContractLineItemId ? CostSourceType.BID_CONTRACT : CostSourceType.MANUAL;
  }
}

export type ChangeEventLineItemFormInput = Pick<
  SaveChangeEventLineItemInput,
  | "id"
  | "costConfidence"
  | "quantity"
  | "totalPriceInCents"
  | "totalCostInCents"
  | "tradePartnerId"
  | "proposedBidItemId"
  | "proposedBidContractLineItemId"
  | "unitOfMeasureId"
  | "proposedTaskId"
  | "proposedPackageDesignNotes"
> & { fragment: ChangeEventLineItemsTabChangeEventLineItemFragment };

export const changeEventLineItemFormConfig: ObjectConfig<ChangeEventLineItemFormInput> = {
  // Context refreshes with the form but is removed from changedValue before saving. A fragment field
  // would delete the backing property, preventing the model's computed getters from reading it.
  fragment: { type: "value", readOnly: true },
  id: { type: "value" },
  costConfidence: { type: "value" },
  quantity: { type: "value" },
  totalPriceInCents: { type: "value" },
  totalCostInCents: { type: "value" },
  tradePartnerId: { type: "value" },
  proposedBidItemId: { type: "value" },
  proposedBidContractLineItemId: { type: "value" },
  unitOfMeasureId: { type: "value" },
  proposedTaskId: { type: "value" },
  proposedPackageDesignNotes: { type: "value" },
};

/** A stale server relation must not replace the label for an ID the user just selected. */
function selectedRelation<T extends { id: string }>(
  id: string | null | undefined,
  server: T | null | undefined,
  selected: T | undefined,
): T | undefined {
  if ((id ?? undefined) === server?.id) return server ?? undefined;
  return selected?.id === id ? selected : undefined;
}
